import "core-js/modules/web.dom-collections.iterator";
import "core-js/modules/web.dom-collections.iterator";

function ownKeys(object, enumerableOnly) {
  var keys = Object.keys(object);

  if (Object.getOwnPropertySymbols) {
    var symbols = Object.getOwnPropertySymbols(object);
    if (enumerableOnly) symbols = symbols.filter(function (sym) {
      return Object.getOwnPropertyDescriptor(object, sym).enumerable;
    });
    keys.push.apply(keys, symbols);
  }

  return keys;
}

function _objectSpread(target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = arguments[i] != null ? arguments[i] : {};

    if (i % 2) {
      ownKeys(Object(source), true).forEach(function (key) {
        _defineProperty(target, key, source[key]);
      });
    } else if (Object.getOwnPropertyDescriptors) {
      Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
      ownKeys(Object(source)).forEach(function (key) {
        Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
      });
    }
  }

  return target;
}

function _defineProperty(obj, key, value) {
  if (key in obj) {
    Object.defineProperty(obj, key, {
      value: value,
      enumerable: true,
      configurable: true,
      writable: true
    });
  } else {
    obj[key] = value;
  }

  return obj;
}
/**
 * Component: Carousel
 * ------------------------------------------------------------------------------
 * An interface to create carousel objects using the Swiper plugin.
 * - https://github.com/nolimits4web/swiper
 *
 * @namespace carousel
 */


import Swiper from 'swiper/js/swiper.min';
import { debounce, extendDefaults, isElementInViewport, on } from '~/helpers/utils';
/**
 * DOM selectors.
 */

const selectors = {
  cell: '[js-carousel="cell"]',
  container: '[js-carousel="container"]'
};
/**
 * Create a new carousel instance.
 * @param {String} selector - DOM selector.
 * @param {Object} config - Swiper carousel settings.
 */

export default ((selector, config) => {
  /**
   * DOM node selectors.
   */
  const nodeSelectors = {
    container: document.querySelector(selector),
    cells: [...document.querySelectorAll(config.cellSelector || selectors.cell)]
  };
  /**
   * Instance default settings.
   */

  if (!nodeSelectors.container) {
    throw new Error("Could not find carousel selector: ".concat(selector));
  }

  const namespace = nodeSelectors.container.id || selector;
  const defaults = {
    aria: true,
    swiper: {
      a11y: {
        enabled: true
      },
      autoplay: false,
      centeredSlides: false,
      keyboard: {
        enabled: true,
        onlyInViewport: true
      },
      loop: true,
      on: {},
      slidesPerView: 1,
      spaceBetween: 16,
      watchSlidesVisibility: true
    }
  };
  /**
   * Instance settings.
   */

  const swiperSettings = extendDefaults(defaults.swiper, config.swiper);
  config.swiper = swiperSettings;
  const settings = extendDefaults(defaults, config);
  /**
   * Instance globals.
   */

  let carousel = {};
  /**
   * Initialise carousel component.
   */

  function init() {
    if (nodeSelectors.cells.length <= 1) {
      return;
    }

    carousel = new Swiper(nodeSelectors.container, _objectSpread(_objectSpread({}, settings.swiper), {}, {
      on: {
        init: () => {
          /**
           * Offsetting event emitter to trigger.
           */
          window.setTimeout(() => {
            Frame.EventBus.emit("Carousel-".concat(namespace, ":ready"), {
              container: nodeSelectors.container,
              selectedIndex: getSelectedIndex()
            });
          }, 0);
        },
        resize: () => {
          Frame.EventBus.emit("Carousel-".concat(namespace, ":resize"));
        },
        transitionEnd: () => {
          Frame.EventBus.emit("Carousel-".concat(namespace, ":settle"), {
            container: nodeSelectors.container,
            selectedIndex: getSelectedIndex()
          });
        }
      }
    }));

    if (settings.aria) {
      setARIAEventListeners();
    }
  }
  /**
   * Set listeners for Carousel and update accessibility markup.
   */


  function setARIAEventListeners() {
    Frame.EventBus.listen("Carousel-".concat(namespace, ":ready"), section => {
      updateVisibleCells(section);
      on('resize', debounce(() => updateVisibleCells(section), 500));
    });
    Frame.EventBus.listen("Carousel-".concat(namespace, ":settle"), section => {
      updateVisibleCells(section);
    });
  }
  /**
   * Sets tabindex to -1 to links from slides that are not active.
   * @param {HTMLElement} section - Swiper slider.
   */


  function updateVisibleCells(section) {
    const {
      container
    } = section;
    [...container.querySelectorAll(selectors.cell)].forEach(cell => {
      if (!isElementInViewport(container, cell)) {
        updateCellARIA(cell, true, -1);
        return;
      }

      updateCellARIA(cell);
    });
  }
  /**
   * Checks if slide is within Swiper viewport and updates the tabbable
   * elements tabindex value.
   * @param {HTMLElement} cell - Swiper slide.
   * @param {Boolean} isHidden - If slide is not visible in viewport.
   * @param {Number} tabindexValue - Tabindex value.
   */


  function updateCellARIA(cell) {
    let isHidden = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : false;
    let tabindexValue = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : 0;
    const tabbableElements = cell.querySelectorAll('a, button');
    cell.setAttribute('aria-hidden', isHidden);

    if (tabbableElements.length) {
      [...tabbableElements].forEach(link => {
        link.setAttribute('tabindex', tabindexValue);
      });
    }
  }
  /**
   * Trigger update events.
   */


  function update() {
    carousel.update();
  }
  /**
   * Trigger resize events.
   */


  function resize() {
    carousel.update();
  }
  /**
   * Destroy instance.
   */


  function destroy() {
    if (Object.keys(carousel).length === 0) {
      return;
    }

    carousel.destroy();
  }
  /**
   * Updated selected slide using index.
   * @param {Number} index - Image slide index to go to.
   * @param {Number} speed - Optional speed parameter.
   */


  function select(index, speed) {
    carousel.slideToLoop(Number(index), speed);
  }
  /**
   * Get the selected index in carousel.
   * @returns {Number}
   */


  function getSelectedIndex() {
    return carousel.realIndex;
  }
  /**
   * Get the Swiper instance of the carousel.
   * @returns {Object}
   */


  function getSwiperInstance() {
    return carousel;
  }
  /**
   * Check if Swiper instance has been initialised.
   */


  function isInitialised() {
    return Object.keys(carousel).length !== 0 && !carousel.destroyed;
  }
  /**
   * Expose public interface.
   */


  return Object.freeze({
    destroy,
    getSelectedIndex,
    getSwiperInstance,
    init,
    isInitialised,
    resize,
    select,
    update
  });
});